import { NavigateFunction } from 'react-router-dom';
import _get from 'lodash/get';
import { InterventionScopeNameEnum, TaskScopeNameEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import InterventionsFilterFieldNames from '@lib/enums/fieldNames/interventionsFilterFieldNames';
import { GetUrlParams } from '@lib/enums/urls';
import useFilterParams from '@lib/hooks/useFilterParams';
import { logger } from '@lib/services/logger';
import { CustomTaskScopeNameEnum } from 'constants/enums';
import { APP_URLS } from 'constants/urls';
import { ScannerActionType } from 'routes/Scanner/enums';
import {
  isValidDocumentationTab,
  isValidInterventionTab,
  isValidTaskTab,
} from './isValidScannerTab';

type FilterFormSetter = (values: Values) => void;
type FilterParamsSetter = ReturnType<typeof useFilterParams>[1];

type HandleSuccessScanActionProps = {
  values: Values;
  scannerAction: ScannerActionType;
  isOnline: boolean;
  tab: string | undefined;
  pathname: string;
  setTasksFilterForm: FilterFormSetter;
  setInterventionsFilterForm: FilterFormSetter;
  setDocumentsFilterForm: FilterFormSetter;
  setFilterParams: FilterParamsSetter;
  navigate: NavigateFunction;
  closeScanner: () => void;
};

export default function handleSuccessScanAction({
  values,
  scannerAction,
  isOnline,
  tab,
  pathname,
  setTasksFilterForm,
  setInterventionsFilterForm,
  setDocumentsFilterForm,
  setFilterParams,
  navigate,
  closeScanner,
}: HandleSuccessScanActionProps) {
  logger.info('Scanner: success action', {
    scannerAction,
    isOnline,
    siteId: _get(values, ['site', 0, 'value']),
    siteName: _get(values, ['site', 0, 'label']),
    siteAreaId: _get(values, ['siteArea', 0, 'value']),
    siteAreaName: _get(values, ['siteArea', 0, 'label']),
    assetId: _get(values, ['asset', 0, 'value']),
    assetName: _get(values, ['asset', 0, 'label']),
  });

  if (!values || Object.keys(values).length === 0) {
    return;
  }

  if (scannerAction === ScannerActionType.FindTasks) {
    setTasksFilterForm(values);
    const preservedTaskTab = isValidTaskTab(tab) ? tab : undefined;
    navigate(
      APP_URLS.app.tasks.index.getPathWithQuery({
        [GetUrlParams.Tab]: isOnline
          ? preservedTaskTab || TaskScopeNameEnum.MyTodoAvailableToComplete
          : CustomTaskScopeNameEnum.Downloads,
      }),
    );
  } else if (scannerAction === ScannerActionType.FindInterventions) {
    setInterventionsFilterForm(values);
    const preservedInterventionTab = isValidInterventionTab(tab)
      ? tab
      : InterventionScopeNameEnum.OpenedMyParticipation;
    navigate(
      APP_URLS.app.interventions.index.getPathWithQuery({
        [GetUrlParams.Tab]: preservedInterventionTab,
      }),
    );
  } else if (scannerAction === ScannerActionType.FindDocuments) {
    setDocumentsFilterForm(values);
    const preservedDocumentationTab = isValidDocumentationTab(tab)
      ? { [GetUrlParams.Tab]: tab }
      : {};
    navigate(
      APP_URLS.app.documentation.index.getPathWithQuery(
        preservedDocumentationTab,
      ),
    );
  } else if (scannerAction === ScannerActionType.CreateIntervention) {
    const assetId = _get(values, [
      InterventionsFilterFieldNames.Asset,
      0,
      'value',
    ]);
    const siteId = _get(values, [
      InterventionsFilterFieldNames.Site,
      0,
      'value',
    ]);
    const siteAreaId = _get(values, [
      InterventionsFilterFieldNames.SiteArea,
      0,
      'value',
    ]);
    if (pathname === APP_URLS.app.interventions.add.path) {
      setFilterParams({
        [GetUrlParams.AssetId]: assetId,
        [GetUrlParams.SiteId]: siteId,
        [GetUrlParams.SiteAreaId]: siteAreaId,
      });
    } else {
      navigate(
        APP_URLS.app.interventions.add.getPathWithQuery({
          assetId,
          siteId,
          siteAreaId,
        }),
      );
    }
  }

  closeScanner();
}
